export type Topic =
  | "Referrals"
  | "Scheduling"
  | "eConsult"
  | "Case Management"
  | "Cell Therapy"
  | "Public Health"
  | "Probation/Correctional Health"
  | "AI & Automation"
  | "Interoperability"

export interface WhitePaper {
  id: string
  title: string
  subtitle: string
  author: string
  date: string // ISO format for sorting
  displayDate: string // Formatted date for display
  readTime: string
  pdfUrl: string
  heroGradient: string
  topics: Topic[]
  featured?: boolean
}

export const WHITE_PAPERS: WhitePaper[] = [
  {
    id: "wp-011",
    title: "The Future of Referral Management in Healthcare: From Fax to AI-Powered Workflows",
    subtitle: "Referrals remain the 'front door' to specialty care, yet referral processes are often outdated, inefficient, and error-prone. Patients frequently experience delays, lost information, or misdirected referrals.",
    author: "EceptionistCX Research Group",
    date: "2025-08-26",
    displayDate: "August 26, 2025",
    readTime: "12 min read",
    pdfUrl: "/PDF/WhitePapers/ReferralCX_WhitePaper.pdf",
    heroGradient: "bg-[radial-gradient(1200px_600px_at_10%_-10%,#e8f1ff_0%,#f3f7ff_30%,#ffffff_65%)]",
    topics: ["Referrals", "Scheduling", "eConsult", "Case Management", "Cell Therapy", "Public Health", "Probation/Correctional Health", "AI & Automation", "Interoperability"],
    featured: true,
  },
  {
    id: "wp-009",
    title: "AI in Healthcare Operations: Practical Applications for Smarter Collaboration and Better Care",
    subtitle: "Exploring practical applications of AI in referral management, scheduling, bed management, staffing, and case prioritization to optimize resources and improve patient flow.",
    author: "Eceptionist Research Group",
    date: "2025-08-25",
    displayDate: "August 25, 2025",
    readTime: "12 min read",
    pdfUrl: "/PDF/WhitePapers/AI_HealthcareOps_WhitePaper.pdf",
    heroGradient: "bg-[radial-gradient(1200px_600px_at_10%_-10%,#e8f1ff_0%,#f3f7ff_30%,#ffffff_65%)]",
    topics: ["Referrals", "Scheduling", "Interoperability", "AI & Automation", "Case Management"],
  },
  {
    id: "wp-010",
    title: "Digital Front Doors in Healthcare: Why Scheduling Matters More Than Ever",
    subtitle: "Healthcare scheduling has emerged as one of the most strategic enablers of patient satisfaction, access to care, and provider efficiency. Patients now expect a seamless, consumer-grade digital experience.",
    author: "EceptionistCX Research Group",
    date: "2025-08-25",
    displayDate: "August 25, 2025",
    readTime: "10 min read",
    pdfUrl: "/PDF/WhitePapers/SchedulingCX_WhitePaper.pdf",
    heroGradient: "bg-[radial-gradient(1200px_600px_at_10%_-10%,#e8f1ff_0%,#f3f7ff_30%,#ffffff_65%)]",
    topics: ["Referrals", "Scheduling", "AI & Automation", "Interoperability"],
  },
  {
    id: "wp-012",
    title: "From Paper to Digital: Smarter Referrals for Justice-Involved Individuals",
    subtitle: "Probation agencies across the country still rely on paper-based, manual processes to refer justice-involved individuals to treatment, education, and support programs.",
    author: "EceptionistCX Research Group",
    date: "2025-08-25",
    displayDate: "August 25, 2025",
    readTime: "10 min read",
    pdfUrl: "/PDF/WhitePapers/ProbationCX_WhitePaper.pdf",
    heroGradient: "bg-[radial-gradient(1200px_600px_at_10%_-10%,#e8f1ff_0%,#f3f7ff_30%,#ffffff_65%)]",
    topics: ["Referrals", "Scheduling", "Probation/Correctional Health", "Interoperability"],
  },
  {
    id: "wp-013",
    title: "Breaking Silos: How Integrated Care Coordination Improves Outcomes and Reduces Costs",
    subtitle: "Fragmentation remains one of the greatest challenges in modern healthcare. Patients often navigate across multiple providers and services with little coordination.",
    author: "EceptionistCX Research Group",
    date: "2025-08-25",
    displayDate: "August 25, 2025",
    readTime: "11 min read",
    pdfUrl: "/PDF/WhitePapers/IntegratedCare_WhitePaper.pdf",
    heroGradient: "bg-[radial-gradient(1200px_600px_at_10%_-10%,#e8f1ff_0%,#f3f7ff_30%,#ffffff_65%)]",
    topics: ["Referrals", "Scheduling", "eConsult", "Case Management", "Cell Therapy", "Public Health", "AI & Automation", "Interoperability"],
  },
  {
    id: "wp-014",
    title: "Scaling Cell & Gene Therapy Programs: How Technology Enables Safe, Efficient, and Compliant Workflows",
    subtitle: "Cell and gene therapies, including CAR-T treatments, represent one of the fastest-growing areas of medicine. Yet scaling these programs outside of large academic medical centers remains a challenge.",
    author: "EceptionistCX Research Group",
    date: "2025-08-25",
    displayDate: "August 25, 2025",
    readTime: "15 min read",
    pdfUrl: "/PDF/WhitePapers/CellCX_WhitePaper.pdf",
    heroGradient: "bg-[radial-gradient(1200px_600px_at_10%_-10%,#e8f1ff_0%,#f3f7ff_30%,#ffffff_65%)]",
    topics: ["Referrals", "Scheduling", "Cell Therapy", "AI & Automation", "Interoperability"],
  },
  {
    id: "wp-015",
    title: "From Legacy PAS to Intelligent Scheduling: Integrating AI and Smart Automation in NHS Pathway and Capacity Management",
    subtitle: "Across the NHS, legacy Patient Administration Systems (PAS) continue to anchor scheduling, admissions, and outpatient workflows. While foundational, these systems were never designed to manage complex, multi-pathway scheduling.",
    author: "EceptionistCX | Collaborative Healthcare Platform",
    date: "2025-10-15",
    displayDate: "October 15, 2025",
    readTime: "15 min read",
    pdfUrl: "/PDF/WhitePapers/NHS_Intelligent_Scheduling_WhitePaper_Final.pdf",
    heroGradient: "bg-[radial-gradient(1200px_600px_at_10%_-10%,#e8f1ff_0%,#f3f7ff_30%,#ffffff_65%)]",
    topics: ["Scheduling", "AI & Automation", "Interoperability"],
    featured: false,
  },
  {
    id: "wp-016",
    title: "Digitally Orchestrating Cell & Gene Therapy: Why CellCX Provides the Operational Layer Modern EHR Systems Cannot",
    subtitle: "Cell and gene therapy (CGT) is rapidly transforming from a niche offering into a core service line for leading health systems and academic medical centers. CAR-T, TIL, NK cell therapies, and emerging allogeneic and off‑the‑shelf products bring extraordinary clinical promise—but also extraordinary operational complexity.",
    author: "EceptionistCX | Collaborative Healthcare Platform",
    date: "2025-12-04",
    displayDate: "December 4, 2025",
    readTime: "18 min read",
    pdfUrl: "/PDF/WhitePapers/CellCX_EHR_WhitePaper v2.pdf",
    heroGradient: "bg-[radial-gradient(1200px_600px_at_10%_-10%,#e8f1ff_0%,#f3f7ff_30%,#ffffff_65%)]",
    topics: ["Cell Therapy", "Interoperability", "Case Management"],
  },
  {
    id: "wp-017",
    title: "Bridging the Youth Mental Health Access Gap",
    subtitle: "Why Coordination and Eligibility Automation — Not Capacity Alone — Unlock Timely Care",
    author: "EceptionistCX Research Group",
    date: "2026-01-29",
    displayDate: "January 29, 2026",
    readTime: "14 min read",
    pdfUrl: "/PDF/WhitePapers/Youth_Mental_Health_Access_WhitePaper.pdf",
    heroGradient: "bg-[radial-gradient(1200px_600px_at_10%_-10%,#e8f1ff_0%,#f3f7ff_30%,#ffffff_65%)]",
    topics: ["Referrals", "Scheduling", "Case Management", "Public Health", "Interoperability"],
  },
  {
    id: "wp-018",
    title: "From Funding to Access: Why Public Health Investments Fail Without Operational Infrastructure",
    subtitle: "Bridging the Gap Between Federal Dollars and Real-World Outcomes",
    author: "EceptionistCX Collaborative Healthcare Platform",
    date: "2026-04-07",
    displayDate: "April 7, 2026",
    readTime: "14 min read",
    pdfUrl: "/PDF/WhitePapers/From_Funding_to_Access_WhitePaper.pdf",
    heroGradient: "bg-[radial-gradient(1200px_600px_at_10%_-10%,#e8f1ff_0%,#f3f7ff_30%,#ffffff_65%)]",
    topics: ["Public Health", "Interoperability", "AI & Automation", "Scheduling", "Referrals"],
  },
  {
    id: "wp-019",
    title: "From Eligibility to Engagement: Fixing the Broken Front Door to Care",
    subtitle: "A practitioner’s guide to embedding eligibility earlier, reducing access friction, and converting more referrals into completed visits",
    author: "EceptionistCX Collaborative Healthcare Platform",
    date: "2026-04-21",
    displayDate: "April 21, 2026",
    readTime: "16 min read",
    pdfUrl: "/PDF/WhitePapers/From_Eligibility_to_Engagement_WhitePaper.pdf",
    heroGradient: "bg-[radial-gradient(1200px_600px_at_10%_-10%,#e8f1ff_0%,#f3f7ff_30%,#ffffff_65%)]",
    topics: ["Referrals", "Scheduling", "Interoperability", "AI & Automation", "Public Health"],
  },
]

// Helper function to format dates consistently
export function formatDate(iso: string): string {
  // Parse the date string and create a local date to avoid timezone issues
  const [year, month, day] = iso.split('-').map(Number)
  const date = new Date(year, month - 1, day) // month is 0-indexed
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  })
}

// Helper function to get white paper by ID
export function getWhitePaperById(id: string): WhitePaper | undefined {
  return WHITE_PAPERS.find(paper => paper.id === id)
}

// Helper function to get featured white papers
export function getFeaturedWhitePapers(): WhitePaper[] {
  return WHITE_PAPERS.filter(paper => paper.featured)
}
